import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { client } from "@/services/api/client.gen";
import { TICKETS_ROOT_KEY } from "@/hooks/useSupportTickets";
import type { ApiEnvelope, CreatedSupportTicket } from "@/types/support";

interface ContactType {
  id: number;
  slug: string;
  tk: string;
  sortOrder: number;
}

interface SubmitContactMessageBody {
  contactTypeId: number;
  message: string;
  screenshotBase64?: string;
  pageHtml?: string;
  pageUrl?: string;
  diagnostics?: unknown;
  /**
   * Optional project binding. When the contact form is opened in a project
   * context, this scopes the created ticket to that project (`about_project_id`)
   * so a subsequently-granted consent has a concrete project to reference.
   */
  aboutProjectId?: string;
}

export function useContactTypes(enabled = true) {
  return useQuery({
    queryKey: ["support", "contact-types"],
    queryFn: async () => {
      const result = await client.get({
        url: "/v1/support/contact-types",
        throwOnError: true,
      });
      const response = result.data as { data?: ContactType[] } | undefined;
      return response?.data ?? [];
    },
    staleTime: Infinity,
    enabled,
  });
}

export function useSubmitContactMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: SubmitContactMessageBody) => {
      const result = await client.post({
        url: "/v1/support/contact",
        body,
        throwOnError: true,
      });
      // `POST /v1/support/contact` now creates a full ticket and returns the
      // standard envelope with the created ticket in `data`.
      return result.data as ApiEnvelope<CreatedSupportTicket> | undefined;
    },
    onSuccess: () => {
      // The contact form creates a full support ticket server-side. Invalidate
      // the tickets LIST query (prefix-matches both `includeClosed` variants
      // keyed under `TICKETS_ROOT_KEY`) so `/support` refetches and
      // surfaces the new ticket. Runs centrally for BOTH creation paths: the
      // global modal (profile menu / error reporting) and the in-page "Contact
      // support" button in `SupportTicketsSection`. Invalidation marks the
      // query stale even while it is inactive, so a fresh navigation to the
      // list refetches on mount regardless of its `staleTime`.
      void queryClient.invalidateQueries({ queryKey: TICKETS_ROOT_KEY });
    },
  });
}

/**
 * Grant the support agent consent to view the project a ticket is about.
 *
 * Server-side this creates the consent record plus a viewer-capable
 * `project_team` grant for the branded "Studio1 Support" team, which then
 * surfaces in that project's shares list (see `isSupportShare`). Invoked after
 * a project-scoped ticket is created AND the customer ticked the consent box.
 *
 * The grant is best-effort relative to ticket creation: the caller treats a
 * failure here as non-fatal (the ticket still exists) and surfaces a toast.
 */
export function useGrantSupportAccess() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ ticketId }: { ticketId: string }) => {
      const result = await client.post({
        url: `/v1/support/tickets/${ticketId}/access-grant`,
        throwOnError: true,
      });
      return result.data;
    },
    onSuccess: () => {
      // Refresh every project's shares list so the branded "Studio1 Support"
      // row/banner appears without a manual refetch. Prefix-matches all
      // `["project-shares", projectId]` variants.
      void queryClient.invalidateQueries({ queryKey: ["project-shares"] });
    },
  });
}
